import React, { createContext, useReducer, useEffect } from 'react';
import Reducer from './reducer'
import axios from 'axios';
import MetaTags from 'react-meta-tags';

const initialState = {
  stream: 'myata',
  nowPlaying: false,
  volume: '1',
  audio: new Audio(), // No initial src to prevent Safari "forever loading"
  trackInfo: {
    'myata': { artist: 'YOUR MUSIC! YOUR STATION!', track: 'RADIO MYATA' },
    'gold': { artist: 'YOUR MUSIC! YOUR STATION!', track: 'RADIO MYATA' },
    'myata_hits': { artist: 'YOUR MUSIC! YOUR STATION!', track: 'RADIO MYATA' }
  },
  albumArts: {
    'myata': { url: null, artist: null, track: null },
    'gold': { url: null, artist: null, track: null },
    'myata_hits': { url: null, artist: null, track: null }
  },
  favorites: [],
  isLoading: false
};

const Store = ({ children }) => {
  const [state, dispatch] = useReducer(Reducer, initialState);

  const timerRef = React.useRef(null);

  // Use Ref to access current state inside callbacks/timeouts without triggering re-renders
  const stateRef = React.useRef(state);
  // Always update ref when state changes
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const updateTracksInfo = React.useCallback((force = false) => {
    // Access current state via ref
    const currentState = stateRef.current;

    // Battery optimization: ONLY reduce polling when BOTH conditions are true:
    // 1. Tab is hidden in background
    // 2. Music is NOT playing (check actual audio state, not React state)
    // If music IS playing, we MUST continue updates for browser title
    const isAudioPlaying = !currentState.audio.paused;

    if (!force && document.visibilityState !== 'visible' && !isAudioPlaying) {
      // Tab is hidden AND music is paused - save battery with rare updates
      console.log('Battery saver: tab hidden + music paused, next update in 2 min');
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => updateTracksInfo(), 120000); // 2 minutes
      return;
    }

    // Normal/smart polling in ALL other cases:
    // - Tab is visible (user is watching)
    // - Music is playing (need title updates even if tab hidden)
    axios.get('/api_all_tracks.php')
      .then(function (response) {
        // Step 1: Validate HTTP status
        if (response.status !== 200) {
          console.warn('API returned non-200 status:', response.status);
          throw new Error('Invalid status code');
        }

        // Step 2: Validate content-type (relaxed for dev proxy which may return application/x-httpd-php)
        const contentType = response.headers['content-type'] || '';
        const validTypes = ['application/json', 'application/x-httpd-php', 'text/html'];
        if (!validTypes.some(type => contentType.includes(type))) {
          console.warn('API returned unexpected content-type:', contentType);
          throw new Error('Invalid content type');
        }

        // Step 3: Validate response structure
        if (!response.data || typeof response.data !== 'object') {
          console.warn('API response is not an object:', response.data);
          throw new Error('Invalid response format');
        }

        if (!response.data.data || typeof response.data.data !== 'object') {
          console.warn('API response missing data field:', response.data);
          throw new Error('Missing data field');
        }

        // All validation passed - safe to process
        const { data, server_time } = response.data;
        dispatch({ trackInfo: data, lastUse: new Date() });

        const currentStream = data[currentState.stream];
        let nextInterval = 15;

        if (currentStream && currentStream.ends_at && server_time) {
          const timeToWait = (currentStream.ends_at - server_time) + 5;

          // Different max intervals for different streams
          let maxInterval = 300; // Default: 5 min for Myata/Xtra
          if (currentState.stream === 'gold') {
            maxInterval = 420; // Gold: 7 min (6-min tracks + voice-over)
          }

          nextInterval = Math.max(15, Math.min(maxInterval, timeToWait));
        }

        const isVisible = document.visibilityState === 'visible';
        console.log(`Metadata updated. Tab: ${isVisible ? 'visible' : 'hidden'}, audio.paused: ${currentState.audio.paused}, state.nowPlaying: ${currentState.nowPlaying}, next update in: ${nextInterval}s`);

        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => updateTracksInfo(), nextInterval * 1000);
      })
      .catch((error) => {
        // Graceful error handling - keep current data, retry later
        console.error('Metadata fetch error (site continues normally):', error.message || error);

        // Retry after 30 seconds
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => updateTracksInfo(), 30000);
      });
  }, []); // Stable callback

  useEffect(() => {
    state.audio.volume = state.volume;
  });

  useEffect(() => {
    updateTracksInfo();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateTracksInfo(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimeout(timerRef.current);
    };
  }, [updateTracksInfo]);

  useEffect(() => {
    updateTracksInfo(true);
  }, [state.stream, updateTracksInfo]);

  // Handle Favorites Persistence
  useEffect(() => {
    const savedFavorites = localStorage.getItem('myata_favorites');
    if (savedFavorites) {
      try {
        dispatch({ favorites: JSON.parse(savedFavorites) });
      } catch (e) {
        console.error('Error parsing favorites from localStorage:', e);
      }
    }
  }, [dispatch]);

  useEffect(() => {
    localStorage.setItem('myata_favorites', JSON.stringify(state.favorites));
  }, [state.favorites]);

  const getSiteTitle = () => {
    if (state.nowPlaying) {
      const trackInfo = state.trackInfo ? state.trackInfo[state.stream] : null;
      if (trackInfo && trackInfo.artist != null) {
        return trackInfo.artist + ' - ' + trackInfo.track;
      } else {
        return 'Радио Мята';
      }
    } else {
      return 'Радио Мята';
    }
  }

  return (
    <Context.Provider value={[state, dispatch]}>
      <MetaTags>
        <title>{getSiteTitle()}</title>
      </MetaTags>
      {children}
    </Context.Provider>
  )
};

export const Context = createContext(initialState);
export default Store;
